import { inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BRAND, TellentAdminAppConfig } from '@tellent/types';
import { map, of } from 'rxjs';
import { catchError, distinctUntilChanged, skip, switchMap, tap } from 'rxjs/operators';

import { APP_CONFIG, filterBoolean } from '@recruitee/common';
import { createEffectFactory } from '@recruitee/ngrx';
import { WindowRef } from '@recruitee/user-interface';

import { TellentCurrentUserHttpClient } from './tellent-current-user.http';
import {
  TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
} from './tellent-current-user.store';

@Injectable({ providedIn: 'root' })
export class TellentCurrentUserEffects {
  public effects = createEffectFactory(TellentCurrentUserEntityActions, this.actions$);
  public store = inject(Store);
  public brand = inject(BRAND);
  public windowRef = inject(WindowRef);
  public router = inject(Router);
  public appConfig = inject(<InjectionToken<TellentAdminAppConfig>>APP_CONFIG);

  public fetchCurrentUser$ = this.effects(TellentCurrentUserEntityActions.fetch, () =>
    this.tellentCurrentUserHttpClient.fetchCurrentUser(),
  );

  public changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.updatePassword),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.updatePassword(payload).pipe(
          map(response =>
            TellentCurrentUserEntityActions.updatePasswordSuccess({ payload: response }),
          ),
          catchError(error =>
            of(TellentCurrentUserEntityActions.updatePasswordFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public updateCurrentUserData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.updateCurrentUserData),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.updateCurrentUserData(payload).pipe(
          map(response =>
            TellentCurrentUserEntityActions.updateCurrentUserDataSuccess({
              payload: response,
            }),
          ),
          catchError(error =>
            of(TellentCurrentUserEntityActions.updateCurrentUserDataFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public reloadAfterLanguageChange$ = createEffect(
    () =>
      this.store.select(TellentCurrentUserSelectors.data).pipe(
        filterBoolean,
        map(({ language }) => language),
        distinctUntilChanged(),
        skip(1),
        tap(() => this.windowRef.nativeWindow.location.reload()),
      ),
    { dispatch: false },
  );

  public reloadAfterAppLanguageChange$ = createEffect(
    () =>
      this.store.select(TellentCurrentUserSelectors.data).pipe(
        filterBoolean,
        map(({ appLanguages, language }) => {
          if (this.brand === 'recruitee') return appLanguages?.recruitment;
          if (this.brand === 'kiwi') return appLanguages?.coreHr;
          return language;
        }),
        distinctUntilChanged(),
        skip(1),
        tap(data => {
          this.windowRef.nativeWindow.location.reload();
        }),
      ),
    { dispatch: false },
  );

  public leaveCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentCurrentUserEntityActions.leaveCompany),
      switchMap(({ payload }) =>
        this.tellentCurrentUserHttpClient.leaveCompany(payload.orgGuid).pipe(
          tap(() => this.router.navigateByUrl(this.appConfig.tellentAuthUrl)),
          map(response =>
            TellentCurrentUserEntityActions.leaveCompanySuccess({ payload: response }),
          ),
          catchError(error =>
            of(TellentCurrentUserEntityActions.leaveCompanyFail({ payload: error })),
          ),
        ),
      ),
    ),
  );

  constructor(
    private tellentCurrentUserHttpClient: TellentCurrentUserHttpClient,
    private actions$: Actions,
  ) {}
}
